import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import ReactPlayer from 'react-player';

const CustomDraggableReactPlayer = forwardRef(({
  mediaStream,
  action,
  setAction,
  style = {},
  initialWidth = 180,
  initialHeight = 133,
  minWidth = 120,
  minHeight = 90,
  maxWidth = 1000,
  maxHeight = 1000
}, ref) => {
  // State for position and size
  const [position, setPosition] = useState({ x: 20, y: 20 });
  const [size, setSize] = useState({ width: initialWidth, height: initialHeight });
  
  // References for storing operation info
  const actionRef = useRef(action);
  const dragInfo = useRef({ offset: { x: 0, y: 0 } });
  const resizeInfo = useRef({ 
    startPos: { x: 0, y: 0 },
    startSize: { width: 0, height: 0 }
  });
  
  // DOM element references
  const containerRef = useRef(null);
  const resizeHandleRef = useRef(null);

  // Set initial position at bottom left
  useEffect(() => {
    const bottomY = window.innerHeight - initialHeight - 130;
    setPosition({ x: 20, y: bottomY });
  }, [initialHeight]);
  
  // Keep actionRef in sync with action prop
  useEffect(() => {
    actionRef.current = action;
  }, [action]);
  
  // ---- DRAG FUNCTIONALITY ----
  
  // Start dragging
  const handleStartDrag = (clientX, clientY) => {
    const containerRect = containerRef.current.getBoundingClientRect();
    
    // Calculate offset from mouse/touch position to container top-left corner
    dragInfo.current.offset = {
      x: clientX - containerRect.left,
      y: clientY - containerRect.top
    };
    
    setAction('drag');
  };
  
  // Mouse down handler
  const handleMouseDown = (e) => {
    // Only proceed if left mouse button is clicked
    if (e.button !== 0) return;
    
    // Check if click is on resize handle
    if (resizeHandleRef.current && resizeHandleRef.current.contains(e.target)) {
      handleStartResize(e.clientX, e.clientY);
    } else {
      handleStartDrag(e.clientX, e.clientY);
    }
    
    e.preventDefault();
  };
  
  // Touch start handler
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    
    // Check if touch is on resize handle
    if (resizeHandleRef.current && resizeHandleRef.current.contains(e.target)) {
      handleStartResize(touch.clientX, touch.clientY);
    } else {
      handleStartDrag(touch.clientX, touch.clientY);
    }
  };
  
  // ---- RESIZE FUNCTIONALITY ----
  
  // Start resizing
  const handleStartResize = (clientX, clientY) => {
    resizeInfo.current.startPos = { x: clientX, y: clientY };
    resizeInfo.current.startSize = { ...size };
    setAction('resize');
  };
  
  
  const handleMove = (clientX, clientY) => {
    const currentAction = actionRef.current;
    
    if (currentAction === 'drag') {
      const newX = clientX - dragInfo.current.offset.x;
      const newY = clientY - dragInfo.current.offset.y;
      
      setPosition({
        x: newX,
        y: newY
      });
    } 
    else if (currentAction === 'resize') {
      // Calculate deltas
      const deltaX = clientX - resizeInfo.current.startPos.x;
      const deltaY = clientY - resizeInfo.current.startPos.y;
      
      // Calculate new size with aspect ratio maintained
      const aspectRatio = initialWidth / initialHeight;
      
      // Use the larger delta for more natural corner resizing
      const maxDelta = Math.max(deltaX, deltaY * aspectRatio);
      
      let newWidth = Math.max(
        minWidth, 
        Math.min(maxWidth, resizeInfo.current.startSize.width + maxDelta)
      );
      let newHeight = newWidth / aspectRatio;
      
      // Check height limits
      if (newHeight < minHeight) {
        newHeight = minHeight;
        newWidth = newHeight * aspectRatio;
      } else if (newHeight > maxHeight) {
        newHeight = maxHeight;
        newWidth = newHeight * aspectRatio;
      }
      
      setSize({
        width: Math.round(newWidth),
        height: Math.round(newHeight)
      });
    }
  };
  
  // Mouse move handler
  const handleMouseMove = (e) => {
    console.log("calling mouse ",e);
    
    if (actionRef.current) {
      handleMove(e.clientX, e.clientY);
      e.preventDefault();
      e.stopPropagation();
    }
  };
  
  // Touch move handler
  const handleTouchMove = (e) => {
    if (actionRef.current) {
      const touch = e.touches[0];
      handleMove(touch.clientX, touch.clientY);
      e.preventDefault();
    }
  };
  
  // End dragging/resizing
  const handleEnd = () => {
    setAction(null);
  };
  
  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    handleMouseMove,
    handleTouchMove,
    handleEnd,
    handleMouseDown,
    handleTouchStart,
    handleStartDrag,
    handleStartResize
  }));
  
  // Container styles
  const containerStyle = {
    width: `${size.width}px`,
    height: `${size.height}px`,
    border: '2px solid #007bff',
    borderRadius: '6px',
    overflow: 'hidden',
    backgroundColor: '#000',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
    cursor: action === 'drag' ? 'grabbing' : 'grab',
    position: 'fixed',
    left: `${position.x}px`,
    top: `${position.y}px`,
    zIndex: 1000000000000000,
    ...style
  };

  // Resize handle styles
  const resizeHandleStyle = {
    position: 'absolute',
    bottom: '0',
    right: '0',
    width: '24px',
    height: '24px',
    cursor: 'nwse-resize',
    background: 'rgba(0, 123, 255, 0.2)',
    zIndex: 10,
    borderTop: '1px solid rgba(255, 255, 255, 0.3)',
    borderLeft: '1px solid rgba(255, 255, 255, 0.3)'
  };
  
  const resizeIconStyle = {
    position: 'absolute',
    bottom: '5px',
    right: '5px',
    width: '12px',
    height: '12px',
    borderRight: '2px solid #fff',
    borderBottom: '2px solid #fff'
  };

  return (
    <div 
      ref={containerRef}
      style={containerStyle}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <ReactPlayer
          playsinline
          playIcon={<></>}
          pip={false}
          light={false}
          controls={false}
          muted={true}
          playing={true}
          url={mediaStream}
          width="100%"
          height="100%"
          config={{
            file: {
              attributes: {
                style: {
                  objectFit: 'cover',
                  width: '100%',
                  height: '100%'
                }
              }
            }
          }}
          onError={(err) => {
            console.log("participant video error", err);
          }}
        />
        
        {/* Resize handle */}
        <div 
          ref={resizeHandleRef}
          style={resizeHandleStyle}
        >
          <div style={resizeIconStyle}></div>
        </div>
      </div>
    </div>
  );
});

export default CustomDraggableReactPlayer;